import type { FilePicker } from '@elara/client-core/lib/file-picker';
import type { AuthFormState } from '@elara/client-core/states/AuthFormState';
import type { ChatInputStateManager } from '@elara/client-core/states/ChatInputStateManager';
import type { ChatsState } from '@elara/client-core/states/ChatsState';
import type { DestinationGuideState } from '@elara/client-core/states/DestinationGuideState';
import type { DestinationSearchState } from '@elara/client-core/states/DestinationSearchState';
import type { FlightBookingOptionsState } from '@elara/client-core/states/FlightBookingOptionsState';
import type { HotelGuideState } from '@elara/client-core/states/HotelGuideState';
import type { HotelRoomSearchState } from '@elara/client-core/states/HotelRoomSearchState';
import type { InviteState } from '@elara/client-core/states/InviteState';
import type { ItineraryState } from '@elara/client-core/states/ItineraryState';
import type { MembershipPaymentState } from '@elara/client-core/states/MembershipPaymentState';
import type { NavigationPort } from '@elara/client-core/states/navigation-port';
import type { OnboardingState } from '@elara/client-core/states/OnboardingState';
import type { PlaceGuideState } from '@elara/client-core/states/PlaceGuideState';
import type { PolicyAcceptanceState } from '@elara/client-core/states/PolicyAcceptanceState';
import type { ProductTourState } from '@elara/client-core/states/ProductTourState';
import type { SharedItineraryState } from '@elara/client-core/states/SharedItineraryState';
import type { ShortlistState } from '@elara/client-core/states/ShortlistState';
import type { ShortlistUIState } from '@elara/client-core/states/ShortlistUIState';
import type { StoriesState } from '@elara/client-core/states/StoriesState';
import type { ToastState } from '@elara/client-core/states/ToastState';
import type { TripsState } from '@elara/client-core/states/TripsState';

import type { $AppContainer, AppContainer } from '#AppContainer.js';
import type { AppStatesContainer } from './AppStatesContainer.js';
import type { MessageViewState } from './MessageViewState.js';
import type { NotificationsState } from './NotificationsState.js';
import type { RouteGuard, RouterState } from './RouterState.js';
import type { VersionState } from './VersionState.js';

import { memoize, pick } from 'lodash-es';
import { useContext, useEffect, useRef } from 'react';

import { AppContext } from '../AppContext.js';

export type { memoize, pick } from 'lodash';

const appIds = new WeakMap<$AppContainer, number>();
let nextAppId = 0;

/**
 * Memo key for the `use*Live` hooks: the requested keys scoped to the container. Keying by keys alone
 * handed every container the first one's stores, which breaks any page rendering more than one (e.g.
 * a Storybook docs page where each story provides its own fakes).
 */
function appCacheKey($app: $AppContainer, keys: readonly PropertyKey[]): string {
  let id = appIds.get($app);
  if (id === undefined) {
    id = nextAppId++;
    appIds.set($app, id);
  }
  return `${id}:${keys.map(String).join(',')}`;
}

export function useApp(): AppContainer;
export function useApp<Keys extends keyof AppContainer>(...keys: Keys[]): { [Key in Keys]: AppContainer[Key] };
export function useApp<Keys extends keyof AppContainer>(...keys: Keys[]): { [Key in Keys]: AppContainer[Key] } {
  const $app = useContext(AppContext);
  return useAppLive($app, ...keys);
}

export const useAppLive = memoize(
  <Keys extends keyof AppContainer>($app: $AppContainer, ...keys: Keys[]): { [Key in Keys]: AppContainer[Key] } => {
    console.debug(
      `Rerendering useApp(${keys
        .map((key: Keys) => {
          if (typeof key === 'symbol') {
            return (key as symbol).toString();
          }
          return key;
        })
        .join(', ')})`,
    );
    const app = $app.use;
    if (!app) throw new Error('useApp must be used within AppContainer');
    if (keys.length === 0) return app;
    const result: Record<string | number | symbol, unknown> = {};
    for (const key of keys) {
      const value = app[key];
      if (value === undefined) {
        throw new Error(`State ${key} is not provided to the AppContainer`);
      }
      result[key] = value;
    }
    return result as { [Key in Keys]: AppContainer[Key] };
  },
  ($app, ...keys) => appCacheKey($app, keys),
);

export function useAppStates(): AppStatesContainer;
export function useAppStates<K extends Array<keyof AppStatesContainer>>(
  ...keys: K
): Pick<AppStatesContainer, K[number]>;
export function useAppStates<K extends Array<keyof AppStatesContainer>>(
  ...keys: K
): Pick<AppStatesContainer, K[number]> {
  const $app = useContext(AppContext);
  return useAppStatesLive($app, ...keys);
}

export const useAppStatesLive = memoize(
  <K extends Array<keyof AppStatesContainer>>($app: $AppContainer, ...keys: K): Pick<AppStatesContainer, K[number]> => {
    const provided = $app.use;
    const states = provided.states;
    if (keys.length === 0) {
      return states;
    }
    const result = pick(states, keys);
    return result;
  },
  ($app, ...keys) => appCacheKey($app, keys),
);

export function useSessionState() {
  const { sessionState } = useAppStates('sessionState');
  return sessionState;
}

export function usePhoneLinkState() {
  const { phoneLinkState } = useAppStates('phoneLinkState');
  return phoneLinkState;
}

export function usePhoneRequirementState() {
  const { phoneRequirementState } = useAppStates('phoneRequirementState');
  return phoneRequirementState;
}

export function usePaymentMethodsState() {
  const { paymentMethodsState } = useAppStates('paymentMethodsState');
  return paymentMethodsState;
}

/**
 * Returns the platform {@link FilePicker} port (ELARA-692). Views use this seam
 * to open the file dialog instead of holding their own `<input type=file>` ref.
 */
export function useFilePicker(): FilePicker {
  const { filePicker } = useApp('filePicker');
  return filePicker;
}

/**
 * Returns the current user.
 * @returns The current user.
 * @throws {Error} If the current user is not found.
 */
export function useCurrentUser() {
  const sessionState = useSessionState();
  return sessionState.user;
}

/**
 * Returns the profile state.
 * @returns The profile state.
 * @throws {Error} If the profile state is not found.
 */
export function useUserProfileState() {
  const { userProfileState } = useAppStates('userProfileState');
  return userProfileState;
}

/**
 * Returns the current user's profile.
 * @returns The current user's profile.
 * @throws {Error} If the current user's profile is not found.
 */
export function useCurrentUserProfile() {
  const userProfileState = useUserProfileState();
  return userProfileState?.profile;
}

export function useUIState() {
  const { uiState } = useAppStates('uiState');
  return uiState;
}

export function useAppState() {
  const { appState } = useAppStates('appState');
  return appState;
}
export function useChatInputState(): ChatInputStateManager {
  const { chatInputState } = useAppStates('chatInputState');
  return chatInputState;
}
export function useAuthFormState(): AuthFormState {
  const { authFormState } = useAppStates('authFormState');
  return authFormState;
}
export function useMessageViewState(): MessageViewState {
  const { messageViewState } = useAppStates('messageViewState');
  return messageViewState;
}

export function useSharedItineraryState(): SharedItineraryState {
  const { sharedItineraryState } = useAppStates('sharedItineraryState');
  return sharedItineraryState;
}

export function useTripsState(): TripsState {
  const { tripsState } = useAppStates('tripsState');
  return tripsState;
}

export function useChatsState(): ChatsState {
  const { chatsState } = useAppStates('chatsState');
  return chatsState;
}

export function useCurrentChat() {
  const chatsState = useChatsState();
  return chatsState.current;
}

export function useDestinationGuideState(): DestinationGuideState {
  const { destinationGuideState } = useAppStates('destinationGuideState');
  return destinationGuideState;
}

export function useDestinationSearchState(): DestinationSearchState {
  const { destinationSearchState } = useAppStates('destinationSearchState');
  return destinationSearchState;
}

export function useHotelGuideState(): HotelGuideState {
  const { hotelGuideState } = useAppStates('hotelGuideState');
  return hotelGuideState;
}

export function useHotelRoomSearchState(): HotelRoomSearchState {
  const { hotelRoomSearchState } = useAppStates('hotelRoomSearchState');
  return hotelRoomSearchState;
}

export function usePlaceGuideState(): PlaceGuideState {
  const { placeGuideState } = useAppStates('placeGuideState');
  return placeGuideState;
}

export function useFlightBookingOptionsState(): FlightBookingOptionsState {
  const { flightBookingOptionsState } = useAppStates('flightBookingOptionsState');
  return flightBookingOptionsState;
}

export function useItineraryState(): ItineraryState {
  const { itineraryState } = useAppStates('itineraryState');
  return itineraryState;
}

export function useShortlistState(): ShortlistState {
  const { shortlistState } = useAppStates('shortlistState');
  return shortlistState;
}

export function useStoriesState(): StoriesState {
  const { storiesState } = useAppStates('storiesState');
  return storiesState;
}

export function useShortlistUIState(): ShortlistUIState {
  const { shortlistUIState } = useAppStates('shortlistUIState');
  return shortlistUIState;
}

export function useToastState(): ToastState {
  const { toastState } = useAppStates('toastState');
  return toastState;
}

export function useRouterState(): RouterState {
  const { routerState } = useAppStates('routerState');
  return routerState;
}

/**
 * Hook for programmatic navigation.
 *
 * Returns the {@link NavigationPort} contract (backed by the web router today),
 * wrapping each method so destructured calls keep the right `this` binding.
 */
export function useNavigate(): NavigationPort {
  const routerState = useRouterState();

  return {
    get currentRoute() {
      return routerState.currentRoute;
    },
    get previewTarget() {
      return routerState.previewTarget;
    },
    get guideStack() {
      return routerState.guideStack;
    },
    openGuide: (target, mode) => routerState.openGuide(target, mode),
    goToGuide: (index) => routerState.goToGuide(index),
    closeGuide: () => routerState.closeGuide(),
    setGuideTitle: (title) => routerState.setGuideTitle(title),
    navigate: (path, options) => routerState.navigate(path, options),
    goBack: () => routerState.goBack(),
    canGoBack: () => routerState.canGoBack(),
    setPageTitle: (title) => routerState.setPageTitle(title),
    confirmLeave: (message) => routerState.confirmLeave(message),
    updateQueryParams: (params) => routerState.updateQueryParams(params),
    removeQueryParams: (names) => routerState.removeQueryParams(names),
    updateHashFragment: (params) => routerState.updateHashFragment(params),
    removeHashParams: (names) => routerState.removeHashParams(names),
    doesPathMatch: (path, pattern) => routerState.doesPathMatch(path, pattern),
    addGuard: (guard) => routerState.addGuard(guard),
    removeGuard: (guard) => routerState.removeGuard(guard),
  };
}

/**
 * Hook to get current path
 */
export function useCurrentPath(): string | undefined {
  const routerState = useRouterState();
  return routerState.currentRoute?.path;
}

/**
 * Hook to get current route parameters
 */
export function useRouteParams(): Record<string, string> {
  const routerState = useRouterState();
  return routerState.currentRoute?.params || {};
}

/**
 * Hook to get current query parameters
 */
export function useQueryParams(): Record<string, string> {
  const routerState = useRouterState();
  return routerState.currentRoute?.query || {};
}

/**
 * Hook to add a route guard that prevents navigation when there are unsaved changes
 */
export function useRouteGuard(hasUnsavedChanges: boolean, options?: { message?: string; onBlock?: () => void }) {
  const routerState = useRouterState();
  const guardRef = useRef<RouteGuard | null>(null);
  const optionsRef = useRef(options);

  // Keep options ref up to date without triggering effects
  optionsRef.current = options;

  useEffect(() => {
    // Create guard once on mount
    const guard: RouteGuard = {
      canLeave: (currentRoute, targetRoute) => {
        // Allow navigation to the same route
        if (currentRoute.path === targetRoute.path) {
          return true;
        }

        // Block navigation if there are unsaved changes
        if (optionsRef.current?.onBlock) {
          optionsRef.current.onBlock();
        }

        // Show confirmation dialog (via the NavigationPort seam, not window.confirm directly)
        const message = optionsRef.current?.message || 'You have unsaved changes. Are you sure you want to leave?';
        return routerState.confirmLeave(message);
      },
    };

    guardRef.current = guard;

    // Cleanup on unmount
    return () => {
      routerState.removeGuard(guard);
      guardRef.current = null;
    };
  }, [routerState]);

  // Separate effect to add/remove guard based on hasUnsavedChanges
  useEffect(() => {
    if (!guardRef.current) return;

    if (hasUnsavedChanges) {
      routerState.addGuard(guardRef.current);
    } else {
      routerState.removeGuard(guardRef.current);
    }
  }, [hasUnsavedChanges, routerState, guardRef]);
}

/**
 * Hook to update page title dynamically
 */
export function usePageTitle() {
  const routerState = useRouterState();

  return { setTitle: (title: string) => routerState.setPageTitle(title) };
}

/**
 * Hook to access version state for update detection
 */
export function useVersionState(): VersionState {
  const { versionState } = useAppStates('versionState');
  return versionState;
}

export function useInviteState(): InviteState {
  const { inviteState } = useAppStates('inviteState');
  return inviteState;
}

export function useOnboardingState() {
  const { onboardingState } = useAppStates('onboardingState');
  return onboardingState as OnboardingState;
}

export function useMembershipPaymentState(): MembershipPaymentState {
  const { membershipPaymentState } = useAppStates('membershipPaymentState');
  return membershipPaymentState;
}

export function usePolicyAcceptanceState(): PolicyAcceptanceState {
  const { policyAcceptanceState } = useAppStates('policyAcceptanceState');
  return policyAcceptanceState;
}

export function useProductTourState(): ProductTourState {
  const { productTourState } = useAppStates('productTourState');
  return productTourState as ProductTourState;
}

export function useNotificationsState(): NotificationsState {
  const { notificationsState } = useAppStates('notificationsState');
  return notificationsState;
}
